import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["slide", "progress", "counter", "dotsContainer"]
  static values = { index: { type: Number, default: 0 } }

  connect() {
    this.totalSlides = this.slideTargets.length
    this.buildDots()

    // Read initial slide from URL hash (#slide-3 → index 2)
    const initial = this.slideFromHash()
    this.showSlide(initial, false)

    this.boundKeydown = this.handleKeydown.bind(this)
    this.boundHashChange = this.handleHashChange.bind(this)
    document.addEventListener("keydown", this.boundKeydown)
    window.addEventListener("hashchange", this.boundHashChange)
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundKeydown)
    window.removeEventListener("hashchange", this.boundHashChange)
  }

  handleKeydown(event) {
    switch (event.key) {
      case "ArrowRight":
      case " ":
        event.preventDefault()
        this.next()
        break
      case "ArrowLeft":
        event.preventDefault()
        this.previous()
        break
      case "Escape":
        event.preventDefault()
        // Close any open dropdown first; only exit presentation if nothing was open
        const openMenu = this.element.querySelector('[data-dropdown-target="menu"]:not(.hidden)')
        if (openMenu) {
          openMenu.classList.add("hidden")
        } else {
          this.exit()
        }
        break
    }
  }

  handleHashChange() {
    const index = this.slideFromHash()
    if (index !== this.indexValue) {
      this.showSlide(index, false)
    }
  }

  slideFromHash() {
    const match = window.location.hash.match(/^#slide-(\d+)$/)
    if (match) {
      const num = parseInt(match[1], 10)
      if (num >= 1 && num <= this.totalSlides) return num - 1
    }
    return 0
  }

  next() {
    if (this.indexValue < this.totalSlides - 1) {
      this.indexValue++
      this.showSlide(this.indexValue)
    }
  }

  previous() {
    if (this.indexValue > 0) {
      this.indexValue--
      this.showSlide(this.indexValue)
    }
  }

  goToSlide(index) {
    this.showSlide(index)
  }

  openCompare(event) {
    event.preventDefault()
    const link = event.currentTarget
    const url = `${link.href}#slide-${this.indexValue + 1}`
    window.open(url, "_blank")
  }

  exit() {
    const exitUrl = this.element.dataset.presentationExitUrl
    if (exitUrl) {
      Turbo.visit(exitUrl)
    } else {
      history.back()
    }
  }

  buildDots() {
    if (!this.hasDotsContainerTarget) return

    this.dots = []
    for (let i = 0; i < this.totalSlides; i++) {
      const dot = document.createElement("button")
      dot.className = "h-1.5 w-1.5 rounded-full bg-gray-300 transition-all duration-300 hover:bg-gray-500"
      dot.addEventListener("click", () => this.goToSlide(i))
      this.dotsContainerTarget.appendChild(dot)
      this.dots.push(dot)
    }
  }

  showSlide(index, updateHash = true) {
    this.indexValue = index

    this.slideTargets.forEach((slide, i) => {
      if (i === index) {
        slide.style.opacity = "1"
        slide.style.pointerEvents = "auto"
        slide.style.zIndex = "1"
      } else {
        slide.style.opacity = "0"
        slide.style.pointerEvents = "none"
        slide.style.zIndex = "0"
      }
    })

    // Update URL hash without triggering hashchange loop
    if (updateHash) {
      history.replaceState(null, "", `#slide-${index + 1}`)
    }

    // Progress bar
    const progress = ((index + 1) / this.totalSlides) * 100
    if (this.hasProgressTarget) {
      this.progressTarget.style.width = `${progress}%`
    }

    // Counter
    if (this.hasCounterTarget) {
      this.counterTarget.textContent = `${index + 1} / ${this.totalSlides}`
    }

    // Dots
    if (this.dots) {
      this.dots.forEach((dot, i) => {
        if (i === index) {
          dot.classList.remove("bg-gray-300", "h-1.5", "w-1.5")
          dot.classList.add("bg-gray-900", "h-2", "w-2")
        } else {
          dot.classList.remove("bg-gray-900", "h-2", "w-2")
          dot.classList.add("bg-gray-300", "h-1.5", "w-1.5")
        }
      })
    }
  }
}
